/**
 * 对局控制器 —— 驱动 AI 自动行动
 *
 * 设计：
 *   · 自家：等待玩家操作（UI 点击）
 *   · 他家：自动决策（PushFoldAI）+ 动画延迟
 *
 * 循环：状态变化 → useEffect → 判断该谁行动 → 调度
 */

import { useCallback, useEffect, useRef } from 'react';
import { apply } from '@mahjong/engine/src/game/engine.js';
import { canApply, canRon, canTsumo, resolveClaimPriority } from '@mahjong/engine/src/game/rules.js';
import {
  recordPass,
  clearTemporaryFuriten,
  emptyTracker,
  type FuritenTracker,
} from '@mahjong/engine/src/game/furiten.js';
import { PushFoldAI } from '@mahjong/engine/src/game/ai.js';
import { settleDraw } from '@mahjong/engine/src/game/flow.js';
import type { GameState, Seat } from '@mahjong/engine/src/game/state.js';
import type { Tile } from '@mahjong/engine/src/tile.js';
import { dealerSeat, nextSeat } from '@mahjong/engine/src/game/state.js';
import type { Action } from '@mahjong/engine/src/game/rules.js';

/** AI 思考延迟（毫秒）*/
const AI_THINK_MS = 520;

/** 座位名 */
const SEAT_NAMES = ['自家', '下家', '对家', '上家'] as const;

/** 每座位一个 AI 实例 */
const AI_PER_SEAT = [new PushFoldAI(), new PushFoldAI(), new PushFoldAI(), new PushFoldAI()];

export interface UseGameDriverOptions {
  /** 当前游戏状态 */
  state: GameState;
  /** 自家座位 */
  selfSeat: Seat;
  /** 是否启用自动推进 */
  enabled: boolean;
  /** 状态更新 */
  setState: React.Dispatch<React.SetStateAction<GameState>>;
  /** 日志 */
  onLog: (msg: string) => void;
  /** 自家当前有可响应动作（吃/碰/杠/荣和）→ claim 阶段暂停等玩家点按钮。
   *  ⚠️ 必须由 useGame.pendingResponse 派生（单一事实源）。若在此另算一套判定，
   *     一旦与「响应栏渲染条件」不一致，就会出现「driver 停等玩家 · 玩家却没按钮」→ 对局死锁。*/
  selfResponsePending?: boolean;
}

export function useGameDriver({
  state,
  selfSeat,
  enabled,
  setState,
  onLog,
  selfResponsePending,
}: UseGameDriverOptions) {
  const timerRef = useRef<number | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // 日志回调也存 ref（避免依赖导致 effect 重跑）
  const logRef = useRef(onLog);
  logRef.current = onLog;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (fn: () => void, delay = AI_THINK_MS) => {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        fn();
      }, delay);
    },
    [clearTimer],
  );

  useEffect(() => clearTimer, [clearTimer]);

  // ════════════════════════════════════════════════════════════════
  // 一所（他家摸牌后打牌）
  // ════════════════════════════════════════════════════════════════

  const actDiscard = useCallback(
    (s: GameState) => {
      const seat = s.activeSeat;
      const ai = AI_PER_SEAT[seat];
      const player = s.players[seat];
      const ctx = { state: s, seat };

      // 自摸和牌
      if (canTsumo(s).legal && ai.shouldTsumo(ctx)) {
        logRef.current(`${SEAT_NAMES[seat]} 自摸和了！`);
        setState(apply(s, { type: 'tsumo' }));
        return;
      }

      // 暗杠
      const kanTile = ai.kan?.(ctx);
      if (kanTile && canApply(s, { type: 'kan', kind: 'ankan', tile: kanTile }, seat).legal) {
        logRef.current(`${SEAT_NAMES[seat]} 暗杠`);
        setState(apply(s, { type: 'kan', kind: 'ankan', tile: kanTile }));
        return;
      }

      // 立直
      const rc = ai.riichi?.(ctx);
      if (rc && canApply(s, { type: 'discard', tile: rc.tile, riichi: true }, seat).legal) {
        logRef.current(`${SEAT_NAMES[seat]} 立直`);
        setState(apply(s, { type: 'discard', tile: rc.tile, riichi: true }));
        return;
      }

      // 普通打牌（失败则回退到首张合法牌）
      let tile = ai.discard(ctx);
      if (!canApply(s, { type: 'discard', tile }, seat).legal) {
        const fallback = player.hand.find((t) =>
          canApply(s, { type: 'discard', tile: t }, seat).legal,
        );
        if (!fallback) {
          logRef.current(`⚠️ ${SEAT_NAMES[seat]} 无合法打牌 → 流局`);
          setState({ ...s, phase: 'over', result: settleDraw(s, 'exhaustive') });
          return;
        }
        tile = fallback;
      }
      logRef.current(`${SEAT_NAMES[seat]} 打 ${tile.num}${tile.suit}`);
      setState(apply(s, { type: 'discard', tile }));
    },
    [setState],
  );

  // ════════════════════════════════════════════════════════════════
  // claim 阶段（鸣牌/荣和）
  // ════════════════════════════════════════════════════════════════

  const actClaim = useCallback(
    (s: GameState) => {
      const discard = s.lastDiscard;
      const goNext = (tracker?: FuritenTracker) => {
        const discarder = discard?.bySeat ?? s.activeSeat;
        const next = nextSeat(discarder);
        setState(
          apply(
            {
              ...s,
              activeSeat: next,
              lastDiscard: null,
              furitenTracker: tracker ?? s.furitenTracker,
              turn: next === dealerSeat(s.round) ? s.turn + 1 : s.turn,
            },
            { type: 'pass' },
          ),
        );
      };

      if (!discard) {
        goNext();
        return;
      }

      const discarder = discard.bySeat;
      const candidates: Array<{ seat: Seat; action: Action }> = [];
      let tracker: FuritenTracker = s.furitenTracker ?? emptyTracker();

      for (const p of s.players) {
        if (p.seat === discarder) continue;
        const seat = p.seat;
        const ai = AI_PER_SEAT[seat];
        const ctx = { state: s, seat, tracker };

        // 荣和
        if (canRon(s, seat).legal) {
          const wants = seat === selfSeat ? true : ai.respond(ctx, discard.tile, discarder).type === 'ron';
          if (wants) {
            candidates.push({ seat, action: { type: 'ron' } });
            continue;
          }
        }

        // 自家：不自动决策，交给玩家（由 useGame 计算可选动作并显示按钮）
        // ⚠️ 但「能荣和」必须阻塞 —— 不能自动 pass（否则玩家永远无法荣和）
        if (seat === selfSeat) continue;

        const action = ai.respond(ctx, discard.tile, discarder);
        if (action.type === 'pon' || action.type === 'chi' || action.type === 'kan') {
          if (canApply(s, action, seat).legal) candidates.push({ seat, action });
        } else {
          // pass → 可能触发振听
          tracker = recordPass(tracker, seat, discard.tile, p);
        }
      }

      if (candidates.length === 0) {
        goNext(tracker);
        return;
      }

      const winner = resolveClaimPriority(discarder, candidates);
      if (!winner) {
        goNext(tracker);
        return;
      }

      const { seat, action } = winner;

      if (action.type === 'ron') {
        logRef.current(`${SEAT_NAMES[seat]} 荣和！`);
        setState(apply({ ...s, activeSeat: seat }, { type: 'ron' }));
        return;
      }

      const label = action.type === 'pon' ? '碰' : action.type === 'chi' ? '吃' : '杠';
      logRef.current(`${SEAT_NAMES[seat]} ${label}`);
      const after = apply({ ...s, activeSeat: seat }, action);
      setState({
        ...after,
        furitenTracker: clearTemporaryFuriten(after.furitenTracker, seat),
      });
    },
    [selfSeat, setState],
  );

  // ════════════════════════════════════════════════════════════════
  // 主循环
  // ════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!enabled) return;
    const s = stateRef.current;
    if (!s || s.phase === 'over') return;

    // 自家回合（discard）→ 交给玩家，**除非已立直**
    // 立直后只能摸切（摸什么打什么），由 AI 自动执行
    if (s.phase === 'discard' && s.activeSeat === selfSeat) {
      const selfPlayer = s.players[selfSeat];
      if (selfPlayer.riichi) {
        schedule(() => {
          const cur = stateRef.current;
          if (!cur || cur.phase !== 'discard' || cur.activeSeat !== selfSeat) return;
          const p = cur.players[selfSeat];

          // 立直后能自摸 → 自动和牌
          if (canTsumo(cur).legal) {
            logRef.current('自家 立直自摸和了！');
            setState(apply(cur, { type: 'tsumo' }));
            return;
          }

          // 否则摸切：优先打刚摸的牌；若被规则拒绝，则遍历找第一张合法牌
          // ⚠️ 立直后规则只允许「摸切」或「打同种牌」——
          //    不能只试一张就放弃（踩过：drawnTileId 缺失时静默 return → 对局卡死）
          const candidates: Tile[] = [];
          const drawnId = cur.drawnTileId;
          const drawnTile = drawnId != null ? p.hand.find((t) => t.id === drawnId) : undefined;
          if (drawnTile) candidates.push(drawnTile);
          for (const t of p.hand) {
            if (!candidates.some((c) => c.id === t.id)) candidates.push(t);
          }

          const legalTile = candidates.find(
            (t) => canApply(cur, { type: 'discard', tile: t }, selfSeat).legal,
          );
          if (!legalTile) {
            logRef.current('⚠️ 立直中无可打之牌');
            return;
          }
          logRef.current(`立直中：摸切 ${legalTile.num}${legalTile.suit}`);
          setState(apply(cur, { type: 'discard', tile: legalTile }));
        });
        return;
      }
      return; // 未立直 → 等玩家操作
    }

    if (s.phase === 'draw') {
      schedule(() => {
        const cur = stateRef.current;
        if (cur && cur.phase === 'draw') setState(apply(cur, { type: 'draw' }));
      });
      return;
    }

    if (s.phase === 'discard') {
      schedule(() => {
        const cur = stateRef.current;
        if (cur && cur.phase === 'discard' && cur.activeSeat !== selfSeat) actDiscard(cur);
      });
      return;
    }

    if (s.phase === 'claim') {
      // ⚠️ 自家有可响应的动作（吃/碰/杠/荣和）→ 暂停，等玩家选择。
      //    由 useGame.pendingResponse 派生（同一判定源），切勿在此自算。
      if (selfResponsePending) {
        return; // 等玩家点按钮
      }
      schedule(() => {
        const cur = stateRef.current;
        if (cur && cur.phase === 'claim') actClaim(cur);
      }, 400);
      return;
    }
  }, [state, enabled, selfSeat, schedule, setState, actDiscard, actClaim, selfResponsePending]);
}
